import { documentLibraryControllerSetPersonDocumentVisibilityMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface SetPersonDocumentVisibilityVariables {
	dealId: string;
	documentId: string;
	personIds: string[];
	visibleToInvestor: boolean;
}

/**
 * DC-494: shows or hides a document in the investor portal for specific linked persons.
 * The link itself is untouched, so hiding does not undo the association.
 */
export const useSetPersonDocumentVisibility = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		...documentLibraryControllerSetPersonDocumentVisibilityMutation(),
		// Both surfaces read visibility off a document detail response, so refetch rather than
		// tracking it in local component state. They use different endpoints: the Document
		// Library right panel reads the document-library detail, the Document Drawer reads the
		// plain document detail. The drawer's key carries a trailing { transactionId }, so
		// the bare path prefix-matches every variant of it.
		onSuccess: async (_data, { path }) => {
			await queryClient.refetchQueries({
				queryKey: [
					formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_DOCUMENTID_GET, {
						dealId: path.dealId,
						documentId: path.documentId,
					}),
				],
			});

			await queryClient.refetchQueries({
				queryKey: [
					formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_GET, {
						dealId: path.dealId,
						documentId: path.documentId,
					}),
				],
			});
		},
	});

	return {
		mutateAsync: (
			{
				dealId,
				documentId,
				personIds,
				visibleToInvestor,
			}: SetPersonDocumentVisibilityVariables,
			options?: Parameters<typeof mutation.mutateAsync>[1]
		) =>
			mutation.mutateAsync(
				{
					path: { dealId, documentId },
					body: { personIds, visibleToInvestor },
				},
				options
			),
		isPending: mutation.isPending,
	};
};
